import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Link,
  InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility, VisibilityOff,
  CheckCircleOutlined as CheckCircleIcon,
  CelebrationOutlined as CelebrationIcon,
  BoltOutlined as BoltIcon,
  ErrorOutlineOutlined as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  InsightsOutlined as InsightsIcon,
} from '@mui/icons-material';
import axios from 'axios';

const features = [
  { icon: <InsightsIcon />, title: 'Unified analytics', desc: 'Every metric from every platform, one pane of glass.' },
  { icon: <BoltIcon />, title: 'AI agents on demand', desc: 'Purpose-built specialists that answer anything, instantly.' },
  { icon: <TrendingUpIcon />, title: 'Real-time pipelines', desc: 'Fresh data, automated reports, zero manual work.' },
];

const MarketingPanel = ({ variant }) => (
  <Box
    sx={{
      flex: 1,
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      p: 6,
      position: 'relative',
      overflow: 'hidden',
      background: 'var(--gradient-marketing)',
      color: '#fff',
    }}
  >
    <Box sx={{
      position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none',
      background: `
        radial-gradient(circle at 15% 20%, rgba(255,255,255,0.18) 0%, transparent 40%),
        radial-gradient(circle at 85% 75%, rgba(20,184,166,0.3) 0%, transparent 45%),
        radial-gradient(circle at 50% 110%, rgba(139,92,246,0.4) 0%, transparent 50%)
      `,
    }} />

    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: 2,
        background: 'rgba(255,255,255,0.18)',
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px solid rgba(255,255,255,0.25)',
      }}>
        <BoltIcon sx={{ color: '#fff', fontSize: 22 }} />
      </Box>
      <Typography sx={{
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontWeight: 800,
        fontSize: '1.05rem',
        letterSpacing: '-0.02em',
      }}>
        SUPERCHARGEME
      </Typography>
    </Box>

    <Box sx={{ position: 'relative', maxWidth: 480 }}>
      <Typography sx={{
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontSize: { md: '2.5rem', lg: '3rem' },
        fontWeight: 800,
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        mb: 2,
      }}>
        {variant === 'welcome' ? <>Welcome aboard.<br/>Let's get you set up.</> : <>You're invited to<br/>supercharge your team.</>}
      </Typography>
      <Typography sx={{
        fontSize: '1.05rem',
        opacity: 0.85,
        mb: variant === 'welcome' ? 5 : 0,
        lineHeight: 1.55,
        fontWeight: 400,
      }}>
        {variant === 'welcome'
          ? 'Choose a password and you\'ll have instant access to your workspace — data, dashboards, and AI agents all waiting for you.'
          : 'Join your team on SuperChargeMe and start getting answers from your data in seconds.'}
      </Typography>

      {variant === 'welcome' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {features.map((f) => (
            <Box key={f.title} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 1.5,
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                '& svg': { color: '#fff', fontSize: 20 },
              }}>
                {f.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', mb: 0.25 }}>{f.title}</Typography>
                <Typography sx={{ fontSize: '0.875rem', opacity: 0.8, lineHeight: 1.5 }}>{f.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>

    <Typography sx={{ position: 'relative', fontSize: '0.8rem', opacity: 0.65 }}>
      © {new Date().getFullYear()} SuperChargeMe. All rights reserved.
    </Typography>
  </Box>
);

const MobileLogo = () => (
  <Box sx={{
    display: { xs: 'flex', md: 'none' },
    alignItems: 'center', gap: 1.25, mb: 4,
  }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: 1.5,
      background: 'var(--gradient-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <BoltIcon sx={{ color: '#fff', fontSize: 20 }} />
    </Box>
    <Typography sx={{
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontWeight: 800,
      fontSize: '1rem',
      letterSpacing: '-0.02em',
    }}>
      SUPERCHARGEME
    </Typography>
  </Box>
);

const FormPanel = ({ children }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: { xs: 3, sm: 6, md: 8 },
      py: 6,
      bgcolor: 'background.default',
    }}
  >
    <Box sx={{ width: '100%', maxWidth: 420 }}>
      <MobileLogo />
      {children}
    </Box>
  </Box>
);

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid invitation link.');
        setValidatingToken(false);
        return;
      }
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
        const response = await axios.get(`${apiUrl}/api/users/validate-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setError('This invitation link is invalid or has expired.');
        }
      } catch (err) {
        setError('This invitation link is invalid or has expired.');
      } finally {
        setValidatingToken(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return setError('Password is required');
    if (password.length < 6) return setError('Password must be at least 6 characters long');
    if (password !== confirmPassword) return setError('Passwords do not match');

    setLoading(true);
    setError('');
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';
      await axios.post(`${apiUrl}/api/users/accept-invite`, { token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        bgcolor: 'background.default',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography sx={{ color: 'text.secondary' }}>
            Validating invitation...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <MarketingPanel variant="welcome" />
        <FormPanel>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--color-secondary-a10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 3,
          }}>
            <CheckCircleIcon sx={{ color: 'var(--color-secondary)', fontSize: 28 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            Account activated
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            Your account is ready. Sign in to access your workspace.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, borderRadius: 2 }}
          >
            Sign in now
          </Button>
        </FormPanel>
      </Box>
    );
  }

  if (!tokenValid) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <MarketingPanel variant="invite" />
        <FormPanel>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mb: 3,
          }}>
            <ErrorIcon sx={{ color: '#EF4444', fontSize: 28 }} />
          </Box>
          <Typography sx={{
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 700,
            fontSize: '1.75rem',
            letterSpacing: '-0.025em',
            mb: 1,
          }}>
            Invalid invitation
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
            {error || 'This invitation may have expired or been used. Ask your admin for a new one.'}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{ py: 1.5, fontSize: '0.95rem', fontWeight: 600, borderRadius: 2 }}
          >
            Go to sign in
          </Button>
        </FormPanel>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      <MarketingPanel variant="welcome" />
      <FormPanel>
        <Box sx={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--color-primary-a10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 3,
        }}>
          <CelebrationIcon sx={{ color: 'var(--color-primary)', fontSize: 28 }} />
        </Box>

        <Typography sx={{
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontWeight: 700,
          fontSize: '1.75rem',
          letterSpacing: '-0.025em',
          mb: 1,
        }}>
          Activate your account
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.95rem' }}>
          Set a password for <Box component="strong" sx={{ color: 'text.primary' }}>{userEmail}</Box>
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            required fullWidth
            name="password" label="Password" id="invite-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            required fullWidth
            name="confirmPassword" label="Confirm password" id="invite-confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 600,
              borderRadius: 2,
              position: 'relative',
            }}
          >
            {loading ? (
              <CircularProgress
                size={22}
                sx={{
                  color: 'white',
                  position: 'absolute',
                  top: '50%', left: '50%',
                  marginTop: '-11px', marginLeft: '-11px',
                }}
              />
            ) : 'Activate account'}
          </Button>

          <Typography sx={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'text.secondary',
            mt: 4,
          }}>
            Already have an account?{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign in
            </Link>
          </Typography>
        </Box>
      </FormPanel>
    </Box>
  );
};

export default AcceptInvite;
